import {observer} from "mobx-react-lite";
import {
    Checkbox,
    FormControl,
    IconButton,
    Input,
    InputAdornment,
    InputLabel,
    ListItemText,
    MenuItem,
    Select
} from "@mui/material";
import React from "react";
import type {MultiTypedSearchManager} from "./MultiTypedSearchManager";
import {Clear, Search} from "@mui/icons-material";

type Props = {
    id: string,
    className?: string,
    manager: MultiTypedSearchManager,
}
export const MultiTypedSearchComponent: React.FC<Props> = observer(({id, className, manager}) => {
    const searchTypeLabel = 'Search by';
    const singleOption = manager.options.length === 1 ? manager.options[0] : null;
    const hasOptions = manager.options.length > 1;

    const searchFieldLabel = hasOptions ? 'Search term' : singleOption ? `Search ${singleOption.label}` : 'Search';

    const searchString = manager.search.searchString;

    // All options are "(Search anywhere)", so we can hide the secondary list text
    const hasOnlySearchEverywhereOptions = manager.optionKeys.every((k) => k.at(0) !== '^' && k.at(0) !== '=');

    return <div
        className={'multi-typed-search-component' + (className ? ` ${className}` : '') + (hasOptions ? ' has-options' : ' has-no-options')}>

        {hasOptions &&
            <FormControl
                className="typed-search-field-type"
                style={{marginRight: '1.5em', width: '20em'}}>
                <InputLabel>{searchTypeLabel}</InputLabel>
                <Select
                    id={id + '-type'}
                    multiple
                    value={manager.selectedKeys}
                    label={searchTypeLabel}
                    onChange={event => {
                        if (typeof event.target.value === 'string') {
                            manager.setSelectedKeys(event.target.value.split(','));
                        } else {
                            manager.setSelectedKeys(event.target.value);
                        }
                    }}
                    renderValue={keys => {
                        if (keys.length === 0) {
                            return '';
                        }
                        return manager.selectedOptions.map((o) => o.label).reverse().join(', ');
                    }}>
                    {manager.options.map(option => {

                        let searchType = '';
                        if(!hasOnlySearchEverywhereOptions) {
                            const prefix = option.key.at(0);
                            if (prefix === '=') {
                                searchType = '(Search for an exact match)'
                            } else if (prefix === '^') {
                                searchType = '(Match from the beginning)'
                            } else {
                                searchType = '(Search anywhere)'
                            }
                        }

                        // Note: the user is not aware, but ^ and = are much faster than the default search
                        // We would also like to add FULLTEXT search, then all searches are fast enough

                        return <MenuItem key={option.key} value={option.key}>
                            <Checkbox checked={Boolean(manager.selectedOptions.find(o => o.key === option.key))}/>
                            <ListItemText primary={option.label} secondary={searchType}/>
                        </MenuItem>;
                    })}
                </Select>
            </FormControl>
        }

        <FormControl
            className="typed-search-field"
            variant="standard">
            <InputLabel>{searchFieldLabel}</InputLabel>
            <Input
                id={id + '-field'}
                type="text"
                fullWidth={true}
                endAdornment={
                    <InputAdornment position="end">
                        {searchString &&
                            <IconButton
                                aria-label="clear search"
                                onClick={() => manager.search.clearSearch()}
                            >
                                <Clear/>
                            </IconButton>
                        }
                        <IconButton
                            aria-label="search"
                            onClick={() => manager.search.doSearch('click')}
                        >
                            <Search/>
                        </IconButton>
                    </InputAdornment>
                }
                value={searchString}
                onChange={e => manager.search.setSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' ? manager.search.doSearch('enter') : undefined}
                // aria-describedby={id + '-error'}
            />
        </FormControl>
    </div>
})
